import { useState, useRef, useCallback, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { filesAPI } from '../services/api';

export const ALLOWED_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac', '.mp4', '.mov', '.avi', '.webm', '.mkv'];

const MAX_SIZE = 3 * 1024 * 1024 * 1024; // 3GB

export const LANGUAGE_OPTIONS = [
  { code: '', label: 'Auto-detect' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'pl', label: 'Polish' },
];

export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function useFileUpload(onComplete) {
  const { user, refreshUser } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [error, setError] = useState(null);

  const isBatchActive = files.some(f => f.status !== 'pending');
  const allDone = files.length > 0 && files.every(f => f.status === 'completed' || f.status === 'failed');

  const updateFile = (fileId, updates) => {
    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, ...updates } : f));
  };

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }

    if (file.size > MAX_SIZE) {
      return `File too large. Maximum size: 3GB`;
    }

    return null;
  };

  const handleFiles = (newFiles) => {
    setError(null);
    const fileArray = Array.from(newFiles);

    if (fileArray.length === 0) return;

    const availableBytes = user?.storage?.available_bytes ?? Infinity;
    const alreadySelectedSize = files.reduce((sum, f) => sum + f.file.size, 0);
    let runningTotal = alreadySelectedSize;

    const newEntries = [];

    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        newEntries.push({
          id: crypto.randomUUID(),
          file,
          status: 'failed',
          uploadProgress: 0,
          transcriptId: null,
          error: validationError,
        });
        continue;
      }

      if (runningTotal + file.size > availableBytes) {
        newEntries.push({
          id: crypto.randomUUID(),
          file,
          status: 'failed',
          uploadProgress: 0,
          transcriptId: null,
          error: `Not enough storage. You have ${user.storage.used_formatted} of ${user.storage.limit_formatted} used.`,
        });
        continue;
      }

      runningTotal += file.size;
      newEntries.push({
        id: crypto.randomUUID(),
        file,
        status: 'pending',
        uploadProgress: 0,
        transcriptId: null,
        error: null,
      });
    }

    setFiles(prev => [...prev, ...newEntries]);
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, user]);

  const handleChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleUpload = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setError(null);

    for (const fileEntry of pendingFiles) {
      updateFile(fileEntry.id, { status: 'uploading', uploadProgress: 0 });

      try {
        const response = await filesAPI.upload(
          fileEntry.file,
          (progress) => {
            updateFile(fileEntry.id, { uploadProgress: progress });
          },
          selectedLanguage || null
        );

        updateFile(fileEntry.id, {
          status: 'processing',
          transcriptId: response.data.id,
          uploadProgress: 100,
        });
      } catch (err) {
        updateFile(fileEntry.id, {
          status: 'failed',
          error: err.response?.data?.detail || 'Upload failed',
        });
      }
    }
  };

  const handleComplete = () => {
    refreshUser();
    if (onComplete) {
      onComplete();
    }
  };

  const handleReset = () => {
    setFiles([]);
    setSelectedLanguage('en');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    user,
    fileInputRef,
    dragActive,
    files,
    selectedLanguage,
    setSelectedLanguage,
    isBatchActive,
    allDone,
    error,
    removeFile,
    updateFile,
    handleDrag,
    handleDrop,
    handleChange,
    handleUpload,
    handleComplete,
    handleReset,
  };
}
